import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Globe, Upload, Plus, Trash2, ExternalLink, Save, Eye } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const WEEKDAYS = [["0", "Seg"], ["1", "Ter"], ["2", "Qua"], ["3", "Qui"], ["4", "Sex"], ["5", "Sáb"], ["6", "Dom"]];

const readFile = (file, cb) => {
  if (file.size > 1200000) return toast.error("Imagem muito grande (máx. ~1MB).");
  const r = new FileReader();
  r.onload = () => cb(r.result);
  r.readAsDataURL(file);
};

export const PerfilPublico = () => {
  const token = localStorage.getItem("veloclin_token");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [p, setP] = useState(null);
  const photoRef = useRef(null);

  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/api/solo/profile`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => {
        const prof = d.profile || {};
        setP({ weekly_availability: {}, slot_minutes: 30, modality: "ambos", ...prof, published: true });
      })
      .catch(() => toast.error("Erro ao carregar perfil"))
      .finally(() => setLoading(false));
  }, [token]);

  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const wk = p?.weekly_availability || {};

  const addBlock = (day) => set("weekly_availability", { ...wk, [day]: [...(wk[day] || []), { from: "09:00", to: "12:00" }] });
  const rmBlock = (day, i) => set("weekly_availability", { ...wk, [day]: (wk[day] || []).filter((_, j) => j !== i) });
  const setBlock = (day, i, key, val) => set("weekly_availability", { ...wk, [day]: (wk[day] || []).map((b, j) => (j === i ? { ...b, [key]: val } : b)) });

  const save = async () => {
    if (!token) return toast.error("Faça login em uma conta Solo real para salvar.");
    if (!p.slug || p.slug.trim().length < 3) return toast.error("Informe o endereço da sua página (mín. 3 caracteres).");
    setSaving(true);
    try {
      const r = await fetch(`${API}/api/solo/profile`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          slug: p.slug, display_name: p.display_name || "", specialty: p.specialty || "",
          bio: p.bio || "", photo_url: p.photo_url || "", cover_url: p.cover_url || "",
          modality: p.modality || "ambos", price_presencial: Number(p.price_presencial) || 0,
          price_online: Number(p.price_online) || 0, address: p.address || "",
          slot_minutes: Number(p.slot_minutes) || 30, weekly_availability: wk, published: true,
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || "Erro ao salvar");
      setP((s) => ({ ...s, ...d.profile }));
      toast.success("Página publicada e no ar!", { description: `veloclin.com.br/${d.profile.slug}` });
    } catch (e) {
      toast.error("Não foi possível salvar", { description: e.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Carregando…</div>;
  if (!p) return <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-800" data-testid="perfil-no-token">Entre com uma conta Solo para configurar sua página pública.</div>;

  const publicUrl = p.slug ? `/${p.slug}` : null;

  return (
    <div className="space-y-5" data-testid="perfil-publico">
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <Globe className="h-5 w-5 text-blue-600" />
          <div>
            <h3 className="font-heading text-base font-bold text-slate-900">Minha Página de Vendas</h3>
            <p className="text-xs text-slate-500">Sua landing pública para atrair e agendar pacientes.</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span data-testid="perfil-live-badge" className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
            <Globe className="h-3.5 w-3.5" /> No ar ao salvar
          </span>
          {publicUrl && (
            <a data-testid="perfil-view-link" href={publicUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50">
              <Eye className="h-3.5 w-3.5 text-blue-600" /> Ver página <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>

      {/* Endereço + capa/foto */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4">
          <Label className="text-xs font-semibold text-slate-600">Endereço da página (você digita)</Label>
          <div className="mt-1.5 flex items-center overflow-hidden rounded-xl border border-slate-200">
            <span className="bg-slate-50 px-3 py-2.5 text-xs text-slate-400">veloclin.com.br/</span>
            <input data-testid="perfil-slug" value={p.slug || ""} onChange={(e) => set("slug", e.target.value)} placeholder="dra-camila-ferraz" className="min-w-0 flex-1 px-3 py-2.5 text-sm outline-none" />
          </div>
        </div>

        {/* foto (estilo Instagram — sem capa) */}
        <div className="flex items-center gap-4 rounded-xl border border-slate-200 bg-white p-4">
          <div className="rounded-full bg-gradient-to-tr from-emerald-500 via-teal-500 to-cyan-500 p-[3px] shadow">
            <div className="h-20 w-20 overflow-hidden rounded-full border-4 border-white bg-slate-100">
              {p.photo_url ? <img src={p.photo_url} alt="foto" className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-2xl font-bold text-slate-400">{(p.display_name || "?").charAt(0)}</div>}
            </div>
          </div>
          <div>
            <p className="text-sm font-semibold text-slate-800">Foto de perfil</p>
            <p className="text-xs text-slate-500">Use uma foto profissional, enquadrada como no Instagram.</p>
            <button data-testid="perfil-photo-btn" onClick={() => photoRef.current?.click()} className="mt-1.5 flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">
              <Upload className="h-3.5 w-3.5" /> Enviar foto
            </button>
          </div>
        </div>
        <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && readFile(e.target.files[0], (u) => set("photo_url", u))} />

        <div className="grid gap-4 sm:grid-cols-2">
          <div><Label className="text-xs font-semibold text-slate-600">Nome de exibição</Label><Input data-testid="perfil-name" value={p.display_name || ""} onChange={(e) => set("display_name", e.target.value)} className="mt-1.5" /></div>
          <div><Label className="text-xs font-semibold text-slate-600">Especialidade</Label><Input data-testid="perfil-specialty" value={p.specialty || ""} onChange={(e) => set("specialty", e.target.value)} className="mt-1.5" /></div>
        </div>
        <div className="mt-4"><Label className="text-xs font-semibold text-slate-600">Sobre mim / experiência</Label><Textarea data-testid="perfil-bio" value={p.bio || ""} onChange={(e) => set("bio", e.target.value)} rows={4} className="mt-1.5 resize-none" placeholder="Conte sua trajetória, formação e diferenciais..." /></div>
      </div>

      {/* Modalidade e preços */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h4 className="font-heading text-sm font-bold text-slate-900">Atendimento & Valores</h4>
        <div className="mt-3 grid gap-4 sm:grid-cols-3">
          <div>
            <Label className="text-xs font-semibold text-slate-600">Modalidade</Label>
            <Select value={p.modality} onValueChange={(v) => set("modality", v)}>
              <SelectTrigger data-testid="perfil-modality" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ambos">Presencial e Online</SelectItem>
                <SelectItem value="presencial">Somente Presencial</SelectItem>
                <SelectItem value="online">Somente Online</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {p.modality !== "online" && <div><Label className="text-xs font-semibold text-slate-600">Valor presencial (R$)</Label><Input data-testid="perfil-price-presencial" type="number" value={p.price_presencial} onChange={(e) => set("price_presencial", e.target.value)} className="mt-1.5" /></div>}
          {p.modality !== "presencial" && <div><Label className="text-xs font-semibold text-slate-600">Valor online (R$)</Label><Input data-testid="perfil-price-online" type="number" value={p.price_online} onChange={(e) => set("price_online", e.target.value)} className="mt-1.5" /></div>}
        </div>
        {p.modality !== "online" && <div className="mt-4"><Label className="text-xs font-semibold text-slate-600">Endereço do consultório (presencial)</Label><Input data-testid="perfil-address" value={p.address || ""} onChange={(e) => set("address", e.target.value)} className="mt-1.5" placeholder="Rua, número — bairro, cidade/UF" /></div>}
      </div>

      {/* Disponibilidade semanal */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <h4 className="font-heading text-sm font-bold text-slate-900">Agenda Semanal</h4>
          <div className="flex items-center gap-2">
            <Label className="text-xs font-semibold text-slate-600">Duração (min)</Label>
            <Input data-testid="perfil-slot-min" type="number" value={p.slot_minutes} onChange={(e) => set("slot_minutes", e.target.value)} className="h-8 w-20" />
          </div>
        </div>
        <p className="mt-1 text-xs text-slate-500">Defina os blocos de horário abertos em cada dia. A página gera os horários automaticamente.</p>
        <div className="mt-4 space-y-3">
          {WEEKDAYS.map(([day, label]) => (
            <div key={day} className="rounded-xl border border-slate-100 bg-slate-50/60 p-3" data-testid={`perfil-day-${day}`}>
              <div className="flex items-center justify-between">
                <span className="text-sm font-bold text-slate-700">{label}</span>
                <button data-testid={`perfil-add-block-${day}`} onClick={() => addBlock(day)} className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50"><Plus className="h-3 w-3" /> Bloco</button>
              </div>
              <div className="mt-2 space-y-2">
                {(wk[day] || []).length === 0 && <p className="text-xs text-slate-300">Fechado</p>}
                {(wk[day] || []).map((b, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input type="time" value={b.from} onChange={(e) => setBlock(day, i, "from", e.target.value)} className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm sm:flex-none" />
                    <span className="text-xs text-slate-400">às</span>
                    <input type="time" value={b.to} onChange={(e) => setBlock(day, i, "to", e.target.value)} className="min-w-0 flex-1 rounded-lg border border-slate-200 px-2 py-1.5 text-sm sm:flex-none" />
                    <button data-testid={`perfil-rm-block-${day}-${i}`} onClick={() => rmBlock(day, i)} className="shrink-0 text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <button data-testid="perfil-save-btn" onClick={save} disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3.5 text-sm font-bold text-white shadow hover:bg-blue-700 disabled:opacity-60">
        <Save className="h-4 w-4" /> {saving ? "Salvando…" : "Salvar e Publicar Página"}
      </button>
    </div>
  );
};
